/**
 * AISessionPanel — inline tracker for Emergent credit consumption.
 *
 * Renders inside a company card when the company is currently focused.
 * Two modes:
 *   1. No active session → input "current Emergent balance" + Start button.
 *   2. Active session → shows start balance + input "balance now" + End/Cancel buttons.
 *
 * After End, the panel keeps a short history (last 3 closed sessions) so
 * the SA can glance at "what just got consumed" without opening a modal.
 *
 * The Emergent dashboard doesn't expose the balance via API — the SA
 * types it once at start, once at end. Everything else is automatic.
 */
import { useEffect, useState } from "react";
import { Loader2, Play, Square, X, Zap, History } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const fmt = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};
const fmtDateShort = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit" }) + " " +
           d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};

export function AISessionPanel({ company, onChanged }) {
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [multiplier, setMultiplier] = useState(3);  // ×3 default
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/companies/${company.id}/ai-sessions?limit=3`);
      setActive(data.active || null);
      setHistory((data.sessions || []).filter((s) => s.ended_at));
    } catch (e) {
      // Silent — card stays usable, just no data.
      setActive(null);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company.id]);

  const start = async (e) => {
    e?.stopPropagation?.();
    const n = parseFloat((startInput || "").toString().replace(/,/g, ""));
    if (!isFinite(n) || n < 0) { toast.error("הקלד/י יתרה נוכחית תקינה"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/companies/${company.id}/ai-session/start`, { start_balance: n });
      setStartInput("");
      toast.success(`סשן AI התחיל · יתרת התחלה ${fmt(n)}`);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "לא ניתן לפתוח סשן");
    } finally { setBusy(false); }
  };

  const end = async (e) => {
    e?.stopPropagation?.();
    const n = parseFloat((endInput || "").toString().replace(/,/g, ""));
    if (!isFinite(n) || n < 0) { toast.error("הקלד/י יתרה נוכחית תקינה"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${company.id}/ai-session/end`,
        { end_balance: n, note: noteInput.trim(), multiplier }
      );
      setEndInput(""); setNoteInput("");
      const delta = Number(data.delta) || 0;
      const charged = Number(data.charged_credits) || 0;
      if (data.is_internal) {
        toast.success(`סשן נסגר · ירדו ${fmt(delta)} קרדיטים (ללא חיוב — FlowUp Core)`);
      } else if (charged > 0) {
        toast.success(`חויב ${fmt(charged)} קרדיטים (${fmt(data.amount_nis)} ₪) · ${fmt(delta)} × ${multiplier}`);
      } else if (delta < 0) {
        toast.success(`עלו ${fmt(-delta)} קרדיטים (טעינה?) · ללא חיוב`);
      } else {
        toast.info("סשן נסגר — 0 קרדיטים");
      }
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "לא ניתן לסגור סשן");
    } finally { setBusy(false); }
  };

  const cancel = async (e) => {
    e?.stopPropagation?.();
    if (!window.confirm("לבטל את הסשן הפתוח?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/companies/${company.id}/ai-session/cancel`, {});
      toast.success("הסשן בוטל");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה");
    } finally { setBusy(false); }
  };

  // Last session delta — pinned big on the card so the SA glances at it
  // while sipping coffee and immediately knows "the last task cost me X".
  const lastDelta = history[0]?.delta;

  return (
    <div
      className="mt-2 rounded-xl border-2 border-amber-300 bg-gradient-to-l from-amber-50 via-orange-50 to-rose-50 p-2.5 space-y-2"
      onClick={(e) => e.stopPropagation()}
      data-testid={`ai-session-panel-${company.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-extrabold text-amber-800">
          <Zap className="h-3.5 w-3.5" />
          סשן AI — מעקב קרדיטים
        </div>
        {loading && <Loader2 className="h-3 w-3 animate-spin text-amber-600" />}
      </div>

      {/* Active session */}
      {active ? (
        <div className="bg-white rounded-lg border border-amber-200 p-2 space-y-2" data-testid={`ai-session-active-${company.id}`}>
          <div className="flex items-center justify-between text-[10px] text-stone-600">
            <span>סשן פתוח · {fmtTime(active.started_at)}</span>
            <span>לפני: <strong className="text-amber-700">{fmt(active.start_balance)}</strong></span>
          </div>
          {/* Billing multiplier — default ×3 per business rule.
              Internal FlowUp Core company never gets charged, but the
              math is still recorded for transparency. */}
          {!company.is_internal && (
            <div className="flex items-center justify-between gap-1.5 bg-stone-50 rounded-md p-1.5">
              <span className="text-[10px] font-bold text-stone-600">חיוב:</span>
              <div className="flex gap-1" data-testid={`ai-session-multiplier-${company.id}`}>
                {[1, 2, 3].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setMultiplier(m); }}
                    className={`text-[11px] font-extrabold rounded px-2.5 py-1 transition ${
                      multiplier === m
                        ? "bg-amber-600 text-white shadow"
                        : "bg-white text-stone-500 hover:text-amber-700 border border-stone-200"
                    }`}
                    data-testid={`ai-session-multiplier-x${m}-${company.id}`}
                  >
                    ×{m}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-1.5">
            <input
              type="number"
              step="0.01"
              dir="ltr"
              value={endInput}
              onChange={(e) => setEndInput(e.target.value)}
              placeholder="יתרה עכשיו"
              className="text-sm font-bold tabular-nums text-center rounded-md border border-amber-300 px-2 py-1.5 focus:outline-none focus:border-amber-500"
              data-testid={`ai-session-end-input-${company.id}`}
            />
            <button
              type="button"
              onClick={end}
              disabled={busy || !endInput}
              className="inline-flex items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white text-xs font-extrabold rounded-md px-2 py-1.5 transition"
              data-testid={`ai-session-end-btn-${company.id}`}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Square className="h-3 w-3" />}
              סיים סשן
            </button>
          </div>
          <input
            type="text"
            maxLength={200}
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value)}
            placeholder="מה עשיתי בסשן הזה? (אופציונלי)"
            className="w-full text-[11px] rounded-md border border-stone-200 px-2 py-1 focus:outline-none focus:border-amber-400"
            data-testid={`ai-session-note-input-${company.id}`}
          />
          <button
            type="button"
            onClick={cancel}
            disabled={busy}
            className="text-[10px] text-stone-500 hover:text-rose-600 inline-flex items-center gap-1"
            data-testid={`ai-session-cancel-btn-${company.id}`}
          >
            <X className="h-3 w-3" /> ביטול סשן
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-1.5" data-testid={`ai-session-start-${company.id}`}>
          <input
            type="number"
            step="0.01"
            dir="ltr"
            value={startInput}
            onChange={(e) => setStartInput(e.target.value)}
            placeholder="יתרה ב-Emergent"
            className="text-sm font-bold tabular-nums text-center rounded-md border border-amber-300 px-2 py-1.5 focus:outline-none focus:border-amber-500"
            data-testid={`ai-session-start-input-${company.id}`}
          />
          <button
            type="button"
            onClick={start}
            disabled={busy || !startInput}
            className="inline-flex items-center justify-center gap-1 bg-amber-600 hover:bg-amber-700 disabled:bg-stone-300 text-white text-xs font-extrabold rounded-md px-2 py-1.5 transition"
            data-testid={`ai-session-start-btn-${company.id}`}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
            התחל סשן
          </button>
        </div>
      )}

      {/* Last closed session — pinned highlight */}
      {history.length > 0 && (
        <div className="rounded-lg bg-emerald-100 border border-emerald-300 p-1.5 text-center" data-testid={`ai-session-last-delta-${company.id}`}>
          <div className="text-[9px] text-emerald-700 font-bold">סשן אחרון:</div>
          <div className="text-base font-extrabold text-emerald-800 tabular-nums">
            {(history[0].delta || 0) > 0
              ? `↓ ${fmt(history[0].delta)} קרדיטים`
              : `↑ ${fmt(-(history[0].delta || 0))}`}
            {history[0].multiplier > 1 && history[0].charged_credits > 0 && (
              <span className="text-[11px] text-emerald-700 mr-1">× {history[0].multiplier}</span>
            )}
          </div>
          {history[0].charged_credits > 0 && !history[0].is_internal && (
            <div className="text-[10px] text-rose-700 font-bold">
              חויב: {fmt(history[0].charged_credits)} ({fmt(history[0].amount_nis)} ₪)
            </div>
          )}
          {history[0].note && (
            <div className="text-[10px] text-emerald-900 truncate" title={history[0].note}>
              {history[0].note}
            </div>
          )}
        </div>
      )}

      {/* Mini history (last 3) */}
      {history.length > 0 && (
        <details className="text-[10px]" data-testid={`ai-session-history-${company.id}`}>
          <summary className="cursor-pointer text-stone-600 hover:text-amber-700 inline-flex items-center gap-1">
            <History className="h-2.5 w-2.5" /> היסטוריית {history.length} סשנים אחרונים
          </summary>
          <ul className="mt-1 space-y-1">
            {history.map((s) => (
              <li key={s.id} className="bg-white/60 rounded px-1.5 py-1 flex items-center justify-between gap-2">
                <span className="text-stone-500 truncate flex-1">{fmtDateShort(s.started_at)}</span>
                <span className="tabular-nums font-bold text-stone-700">
                  {fmt(s.start_balance)} → {fmt(s.end_balance)}
                </span>
                <span className={`tabular-nums font-extrabold ${(s.delta || 0) > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                  {(s.delta || 0) > 0 ? `−${fmt(s.delta)}` : `+${fmt(-(s.delta || 0))}`}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
